import {
  BROWSER_AGENT_DEFAULT_SCHEDULE,
  type BrowserAgentFindingOutcome,
  type BrowserAgentRun,
  type BrowserAgentStep,
  type BrowserAgentStepKind,
  type BrowserAgentWeeklySchedule,
  browserAgentScheduleActiveAt,
} from "@backchannel/tax-contracts";

/**
 * Pure helpers for the browser agent spectator (ADR 0012). The agent itself
 * lives on a VM; the web app only watches. Nothing here may start, publish or
 * approve anything.
 */

/** A running agent that has not reported for this long is considered silent. */
export const BROWSER_AGENT_HEARTBEAT_STALE_MS = 2 * 60_000;
/** The idle agent polls at least every 5 minutes; silence past this is worth a look. */
export const BROWSER_AGENT_POLL_STALE_MS = 12 * 60_000;

export const BROWSER_AGENT_STEP_POLL_MS = 2_500;
export const BROWSER_AGENT_RUN_POLL_MS = 3_000;

/** Local cap for the step log kept in memory by the spectator. */
export const BROWSER_AGENT_STEP_LOG_LIMIT = 500;

export type BrowserAgentStatus =
  | "RUNNING"
  | "PAUSED"
  | "WAITING_WINDOW"
  /** Inside the schedule, VM up, nothing to do right now. */
  | "IDLE"
  /** Inside the schedule but no IOB credentials: the agent cannot sign in. */
  | "NO_CREDENTIALS"
  /** Inside the schedule and the last run failed. */
  | "STOPPED";

/**
 * The agent is "running" only when the latest run is RUNNING. Otherwise it is
 * waiting when the Brasília clock is outside the weekly schedule, and stopped
 * when the schedule is on but nothing runs (manual stop, failure, source
 * disabled).
 */
export function deriveAgentStatus(
  latest: Pick<BrowserAgentRun, "status"> | null | undefined,
  now: Date,
  options: {
    /** Admin-edited weekly schedule; defaults to the contract default. */
    schedule?: BrowserAgentWeeklySchedule;
    /** Kill switch from settings: nothing new starts while paused. */
    paused?: boolean;
    /** False when IOB credentials are known to be missing. */
    credentialsConfigured?: boolean;
  } = {},
): BrowserAgentStatus {
  // The kill switch wins: a run may still be closing its current lookup.
  if (options.paused) return "PAUSED";
  if (latest?.status === "RUNNING") return "RUNNING";
  if (!browserAgentScheduleActiveAt(options.schedule ?? BROWSER_AGENT_DEFAULT_SCHEDULE, now)) {
    return "WAITING_WINDOW";
  }
  if (options.credentialsConfigured === false) return "NO_CREDENTIALS";
  return latest?.status === "FAILED" ? "STOPPED" : "IDLE";
}

export function heartbeatAgeMs(run: Pick<BrowserAgentRun, "heartbeatAt">, now: Date): number {
  return Math.max(0, now.getTime() - Date.parse(run.heartbeatAt));
}

export function isHeartbeatStale(run: Pick<BrowserAgentRun, "heartbeatAt">, now: Date): boolean {
  return heartbeatAgeMs(run, now) > BROWSER_AGENT_HEARTBEAT_STALE_MS;
}

/** Duration of a run; a running one is measured against `now`. */
export function runDurationMs(
  run: Pick<BrowserAgentRun, "startedAt" | "finishedAt">,
  now: Date,
): number {
  const end = run.finishedAt ? Date.parse(run.finishedAt) : now.getTime();
  return Math.max(0, end - Date.parse(run.startedAt));
}

/** "2 h 05 min", "14 min", "38 s". Locale-neutral units used in all three catalogues. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} h ${String(minutes).padStart(2, "0")} min`;
}

export type StepLog = {
  items: BrowserAgentStep[];
  lastSequence: number;
};

/**
 * Merge an incremental `steps?after=` page into the log. Sequences are unique
 * per run; a replayed page never duplicates rows and the log stays ordered by
 * sequence ascending, capped to the most recent entries.
 */
export function mergeSteps(
  previous: StepLog | undefined,
  page: StepLog,
  limit = BROWSER_AGENT_STEP_LOG_LIMIT,
): StepLog {
  const bySequence = new Map<number, BrowserAgentStep>();
  for (const step of previous?.items ?? []) bySequence.set(step.sequence, step);
  for (const step of page.items) bySequence.set(step.sequence, step);
  const items = [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
  const capped = items.length > limit ? items.slice(items.length - limit) : items;
  const lastSequence = Math.max(
    previous?.lastSequence ?? -1,
    page.lastSequence,
    capped.at(-1)?.sequence ?? -1,
  );
  return { items: capped, lastSequence };
}

/** Latest step that carries a frame: the fallback picture when live view is down. */
export function latestFrameStep(steps: readonly BrowserAgentStep[]): BrowserAgentStep | null {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step?.frame) return step;
  }
  return null;
}

export function formatLookupNcm(ncm: string): string {
  return /^\d{8}$/.test(ncm) ? `${ncm.slice(0, 4)}.${ncm.slice(4, 6)}.${ncm.slice(6)}` : ncm;
}

export const STEP_KIND_GLYPH: Record<BrowserAgentStepKind, string> = {
  SESSION: "◎",
  NAVIGATE: "→",
  INPUT: "⌨",
  CLICK: "◉",
  WAIT: "◔",
  READ: "◫",
  EXTRACT: "✓",
  RECOVER: "↺",
  PAUSE: "‖",
  ERROR: "✕",
};

export type OutcomeTone = "positive" | "neutral" | "info" | "caution" | "critical";

export const OUTCOME_PRESENTATION: Record<
  BrowserAgentFindingOutcome,
  { glyph: string; tone: OutcomeTone }
> = {
  ST_FOUND: { glyph: "✓", tone: "positive" },
  NO_ST_FOUND: { glyph: "∅", tone: "neutral" },
  INTERNAL_ST_ONLY: { glyph: "◐", tone: "info" },
  NCM_NOT_FOUND: { glyph: "?", tone: "caution" },
  UNREADABLE: { glyph: "✕", tone: "critical" },
  INCONCLUSIVE: { glyph: "≈", tone: "caution" },
};

/**
 * Frames are served as bytes without a content type the transport can see.
 * Detect the image family from its signature so the blob renders; JPEG is the
 * contract default.
 */
export function sniffImageType(bytes: Uint8Array): string {
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "image/png";
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "image/jpeg";
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  const head = new TextDecoder().decode(bytes.subarray(0, 64)).trimStart();
  if (head.startsWith("<svg") || head.startsWith("<?xml")) return "image/svg+xml";
  return "image/jpeg";
}

/** Viewer reconnect backoff with a fresh ticket each time. */
export function viewerReconnectDelayMs(attempt: number): number {
  const steps = [2_000, 5_000, 10_000, 20_000, 30_000];
  return steps[Math.min(Math.max(attempt, 0), steps.length - 1)] ?? 30_000;
}

/** noVNC URL: the viewer ticket is single-use and travels as a query parameter. */
export function viewerSocketUrl(url: string, ticket: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}ticket=${encodeURIComponent(ticket)}`;
}
